import { useAuth } from '@/hooks/useAuth';
import {
  ROUTES,
  createProtectedRoute,
  createPublicRoute,
  type RouteDefinition,
} from '@/lib/routes';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import { AssociationsListPage } from '@/pages/AssociationsListPage';
import { ContactsListPage } from '@/pages/ContactsListPage';
import { ContactDetailPage } from '@/pages/ContactDetailPage';
import { CrewsListPage } from '@/pages/CrewsListPage';
import { CrewDetailPage } from '@/pages/CrewDetailPage';
import { DiscountsListPage } from '@/pages/DiscountsListPage';
import { ProductsListPage } from '@/pages/ProductsListPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { SchoolsListPage } from '@/pages/SchoolsListPage';
import { UsersListPage } from '@/pages/UsersListPage';
import { EmailTemplatesPage } from '@/pages/EmailTemplatesPage';
import { BedAutomationsPage } from '@/pages/BedAutomationsPage';
import { ReservedCrewNumbersPage } from '@/pages/ReservedCrewNumbersPage';
import { OrphanCrewNumbersPage } from '@/pages/OrphanCrewNumbersPage';
import { PricingPage } from '@/pages/PricingPage';
import { VehiclesListPage } from '@/pages/VehiclesListPage';
import { FormsListPage } from '@/pages/FormsListPage';
import { FormSubmissionsPage } from '@/pages/FormSubmissionsPage';
import { PublicFormPage } from '@/pages/PublicFormPage';
import { PublicAddTeammateFormPage } from '@/pages/PublicAddTeammateFormPage';
import { ProspectsListPage } from '@/pages/ProspectsListPage';
import TasksPage from '@/pages/TasksPage';
import { Routes } from 'react-router-dom';

/**
 * Configuration centralisée du routing
 * Utilise une approche déclarative pour définir les routes
 */

// Définition déclarative des routes
const routeDefinitions: RouteDefinition[] = [
  // Routes d'authentification (publiques)
  { path: ROUTES.AUTH.LOGIN, element: <LoginPage />, protected: false },
  { path: ROUTES.AUTH.FORGOT_PASSWORD, element: <ForgotPasswordPage />, protected: false },
  { path: ROUTES.AUTH.RESET_PASSWORD, element: <ResetPasswordPage />, protected: false },
  { path: ROUTES.AUTH.CALLBACK, element: <AuthCallbackPage />, protected: false },

  // Routes protégées du dashboard
  { path: ROUTES.DASHBOARD.HOME, element: <HomePage />, protected: true },
  { path: ROUTES.DASHBOARD.TASKS, element: <TasksPage />, protected: true },

  // Routes de données
  { path: ROUTES.DATA.PRODUCTS, element: <ProductsListPage />, protected: true },
  { path: ROUTES.DATA.VEHICLES, element: <VehiclesListPage />, protected: true },
  { path: ROUTES.DATA.CREWS.LIST, element: <CrewsListPage />, protected: true },
  { path: '/crews/:id', element: <CrewDetailPage />, protected: true },
  { path: ROUTES.DATA.CONTACTS.LIST, element: <ContactsListPage />, protected: true },
  { path: '/contacts/:id', element: <ContactDetailPage />, protected: true },
  { path: ROUTES.DATA.SCHOOLS, element: <SchoolsListPage />, protected: true },
  { path: ROUTES.DATA.ASSOCIATIONS, element: <AssociationsListPage />, protected: true },
  { path: ROUTES.DATA.DISCOUNTS, element: <DiscountsListPage />, protected: true },
  { path: ROUTES.DATA.FORMS.LIST, element: <FormsListPage />, protected: true },
  { path: '/forms/:formId/submissions', element: <FormSubmissionsPage />, protected: true },
  { path: ROUTES.DATA.PROSPECTS, element: <ProspectsListPage />, protected: true },

  // Routes publiques (formulaires)
  { path: '/public/forms/:slug', element: <PublicFormPage />, protected: false },
  {
    path: '/public/crews/:crewId/add-teammate',
    element: <PublicAddTeammateFormPage />,
    protected: false,
  },

  // Routes admin (réservées aux superadmin)
  {
    path: ROUTES.ADMIN.USERS,
    element: <UsersListPage />,
    protected: true,
    requiredRole: 'superadmin',
  },
  {
    path: ROUTES.ADMIN.EMAIL_TEMPLATES,
    element: <EmailTemplatesPage />,
    protected: true,
    requiredRole: 'superadmin',
  },
  {
    // Ouverte aux admin comme aux superadmin : les RPC de 00179 acceptent les
    // deux rôles, et la clôture d'édition est un acte du service administratif.
    path: ROUTES.ADMIN.BED_AUTOMATIONS,
    element: <BedAutomationsPage />,
    protected: true,
    requiredRole: 'admin',
  },
  {
    path: ROUTES.ADMIN.RESERVED_CREW_NUMBERS,
    element: <ReservedCrewNumbersPage />,
    protected: true,
    requiredRole: 'superadmin',
  },
  {
    path: ROUTES.ADMIN.ORPHAN_CREW_NUMBERS,
    element: <OrphanCrewNumbersPage />,
    protected: true,
    requiredRole: 'admin',
  },
  {
    path: ROUTES.ADMIN.PRICING,
    element: <PricingPage />,
    protected: true,
    requiredRole: 'superadmin',
  },
];

export default function Router() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex w-full justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Routes définies de manière déclarative */}
      {routeDefinitions.map(({ path, element, protected: isProtected, requiredRole }) =>
        isProtected
          ? createProtectedRoute(path, element, requiredRole)
          : createPublicRoute(path, element)
      )}

      {/* Catch-all route pour rediriger vers login */}
      {createPublicRoute('*', <LoginPage />)}
    </Routes>
  );
}
